<script lang="ts">
  import Honeybadger from "$app/utils/honeybadger"
  import { xhr } from "$app/utils"
  import Tiptap from "$app/components/Tiptap.svelte"
  import { timeAgoAbbr, timeAgo } from "$app/utils"
  import { FilePenLine, Trash2 } from "lucide-svelte"
  import Reaction from "$app/components/Reaction.svelte"
  import ReactionsView from "$app/components/ReactionsView.svelte"
  import { appStore } from "$app/store.svelte"
  import { onMount } from "svelte"

  interface Props {
    comment: RecognitionCommentType
    ondelete: (comment: RecognitionCommentType) => void
  }

  let { comment = $bindable(), ondelete }: Props = $props()

  if (comment._marked) {
    let access = document.getElementById(`recognition-comment-${comment.id}`)
    if (access) {
      access.scrollIntoView({ behavior: "smooth" })
    }
  }

  let savingComment = $state(false)
  async function updateComment(content: string): Promise<void> {
    savingComment = true

    try {
      const resp = await xhr.put(
        `/plans/${appStore.plan.id}/recognition_comments/${comment.id}.json`,
        {
          recognition_comment: {
            body: content
          }
        }
      )
      savingComment = false

      comment.body = content
    } catch (e: any) {
      Honeybadger.notify(e)
    } finally {
      edit = false
    }
  }

  let edit = $state(false)

  function updateReaction(reaction: ReactionType) {
    currentReaction = reaction
    comment.reactions = comment.reactions.map((r) => {
      if (r.id === reaction.id) {
        return { ...r, ...reaction }
      }
      return r
    })
  }

  function removeReaction(reaction: ReactionType) {
    comment.reactions = comment.reactions.filter((r) => r.id !== reaction.id)

    currentReaction = { id: null, emoji: null } as ReactionType
  }

  let currentReaction = $state({ user_id: null, emoji: null } as ReactionType)

  onMount(() => {
    let rr = comment.reactions?.find(
      (r) => r.user_id === appStore.current_user.id
    )
    if (rr) currentReaction = rr
  })

  if (!Array.isArray(comment.reactions)) comment.reactions = []
</script>

<li id="recognition-comment-{comment.id}" data-name="recognition-comment">
  <div class="relative pb-4">
    <div class="relative flex items-start space-x-2">
      <img
        class="mt-1 h-8 w-8 rounded-full bg-gray-400 flex items-center justify-center ring-8 ring-white"
        src={comment.avatar_thumb_url}
        alt={comment.name}
      />

      {#if edit}
        <div class="w-full">
          <div>
            <label for="comment" class="sr-only">Comment</label>

            <Tiptap
              id={`comment-${comment.id}`}
              content={comment.body}
              saving={savingComment}
              onsave={({ html }) => updateComment(html)}
              showSaveArrow={true}
              allowEmojis={true}
              allowGiphy={true}
              buttons={[
                ["bold", "italic", "underline", "strike"],
                ["bulletList", "orderedList", "blockquote"],
                ["link", "unlink"]
              ]}
            />
          </div>

          <div class="mt-3 flex items-center justify-end space-x-4">
            <button class="btn btn-secondary" onclick={() => (edit = false)}>
              Cancel Edit Comment
            </button>
          </div>
        </div>
      {:else}
        <div
          class="min-w-0 rounded-lg relative"
          class:border={comment._marked}
          class:border-red-300={comment._marked}
          class:bg-red-50={comment._marked}
          class:p-1={comment._marked}
        >
          <div class="bg-gray-100 rounded-lg pl-3 pr-12 py-2 shadow">
            <div class="absolute right-1 top-1 flex items-center">
              {#if appStore.current_user.id === comment.user_id}
                <button onclick={() => (edit = true)}>
                  <FilePenLine
                    class="w-4 h-4 text-gray-400 hover:text-blue-500"
                  />
                </button>
                <button onclick={() => ondelete(comment)}>
                  <Trash2
                    class="ml-0.5 w-4 h-4 text-gray-400 hover:text-red-500"
                  />
                </button>
              {/if}
            </div>
            <div>
              <p class="text-xs text-gray-800">
                {comment.name}
              </p>
            </div>
            <div class="mt-2 text-gray-800">
              <div class="ProseMirror">
                {@html comment.body}
              </div>
            </div>
          </div>
          <div class="flex items-center gap-4 text-sm py-1">
            <div
              class="ml-2 text-gray-800"
              data-title={timeAgo(comment.created_at)}
            >
              {timeAgoAbbr(comment.created_at)}
            </div>
            <Reaction
              reactionable={comment}
              reaction={currentReaction}
              reactionable_type="RecognitionComment"
              onupdate={(data) => updateReaction(data)}
              oncreate={(data) => {
                comment.reactions = [...comment.reactions, data]
                currentReaction = data
              }}
              ondelete={(data) => removeReaction(data)}
            >
              <span class="text-lwp-accent font-semibold">Like</span>
            </Reaction>
            <div>
              <ReactionsView reactions={comment?.reactions} />
            </div>
          </div>
        </div>
      {/if}
    </div>
  </div>
</li>
